import { useState, type FormEvent } from 'react';
import { ApiError, request } from '@/lib/api.js';
import { useAuthStore } from '@/stores/authStore.js';
import { GlassButton, PageHeader, Section } from '@/components/ui/index.js';

export function SettingsPage(): JSX.Element {
  const admin = useAuthStore((s) => s.admin);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setNotice(null);
    if (newPassword !== confirmPassword) {
      setNotice({ kind: 'error', text: '两次输入的新密码不一致' });
      return;
    }
    if (newPassword.length < 8) {
      setNotice({ kind: 'error', text: '新密码至少 8 位' });
      return;
    }
    if (newPassword === currentPassword) {
      setNotice({ kind: 'error', text: '新密码不能跟当前密码一样' });
      return;
    }
    setSubmitting(true);
    try {
      await request<{ ok: true }>('/admin/auth/change_password', {
        method: 'POST',
        body: {
          current_password: currentPassword,
          new_password: newPassword,
        },
      });
      setNotice({ kind: 'success', text: '密码已更新。下次登录请用新密码。' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'unauthorized') {
        setNotice({ kind: 'error', text: '当前密码不正确' });
      } else {
        setNotice({ kind: 'error', text: err instanceof Error ? err.message : '修改失败' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-xl">
      <PageHeader title="设置" subtitle="账号资料 + 改密。" />

      <Section title="当前账号" className="mb-6">
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-slate-500">用户名</dt><dd>{admin?.username}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">昵称</dt><dd>{admin?.display_name ?? '—'}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">角色</dt><dd className="text-shark-300">{admin?.role}</dd></div>
        </dl>
      </Section>

      <Section title="修改密码">
        <form onSubmit={(event): void => { void handleSubmit(event); }} className="space-y-4">
          <div>
            <label htmlFor="current_pw" className="block text-xs text-slate-400 mb-1">当前密码</label>
            <input
              id="current_pw"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 rounded bg-white/[0.03] border border-white/10 text-slate-200"
            />
          </div>
          <div>
            <label htmlFor="new_pw" className="block text-xs text-slate-400 mb-1">新密码（至少 8 位）</label>
            <input
              id="new_pw"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 rounded bg-white/[0.03] border border-white/10 text-slate-200"
            />
          </div>
          <div>
            <label htmlFor="confirm_pw" className="block text-xs text-slate-400 mb-1">再次输入新密码</label>
            <input
              id="confirm_pw"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 rounded bg-white/[0.03] border border-white/10 text-slate-200"
            />
          </div>
          {notice !== null && (
            <div
              className={`text-sm rounded-lg px-3 py-2 ${
                notice.kind === 'success'
                  ? 'text-felt-400 bg-felt-700/20 border border-felt-500/40'
                  : 'text-rose-300 bg-rose-900/30 border border-rose-700/50'
              }`}
            >
              {notice.text}
            </div>
          )}
          <GlassButton type="submit" variant="primary" disabled={submitting}>
            {submitting ? '保存中…' : '修改密码'}
          </GlassButton>
        </form>
      </Section>
    </div>
  );
}
